import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import defaultContent from "../../../../../content/monthlyPerformanceTotalModals";
import { LeafModal } from "../../../../../common/leaf/LeafModal/LeafModal";
import "../MonthlyPerformanceTotalModals.scss";
import { IconAlert } from "../../../../../common/icons/IconAlert/IconAlert";
import LeafRectangleLoader from "../../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { BonusBreakdownLineData } from "../../../../../common/interfaces/monthlyPerformanceDash";
import { ANA_MARKET_CODES } from "../../../../../common/constants/market-constants";
import { useAppSelector } from "../../../../../store";
import ABOName from "../../../../ABOProfile/ABOName/ABOName";
import LeafLinkButton from "../../../../../common/leaf/LeafLinkButton/LeafLinkButton";
import { IconExportPdf } from "../../../../../common/icons/IconExportPdf/IconExportPdf";
import "./DifferentialBonusModal.scss";
import { BonusInfoFooter } from "../../../../../common/components/BonusInfoFooter/BonusInfoFooter";
import { ErrorTile } from "../../../../../common/components/ErrorTile/ErrorTile";
import {
  BonusPercent,
  DifferentialBonusEntry,
  GroupEntry,
  GroupNonBonusableEntry,
  MonthlyPerformanceResponse,
} from "../../../../../services/MonthlyPerformanceIncome/monthlyPerformanceIncomeAPI.types";
import { storedLocale } from "../../../../Authentication/util";
import { getDisplayName } from "../../../../../common/util/displayName";
import { exportDifferentialBonusPDF } from "../../../../../services/MonthlyPerformanceIncome/monthlyPerformanceIncomeAPI";
import { prepareDifferentialBonusContent } from "./prepareDifferentialBonusContent";

export type Table = {
  aboBonusPercent: string;
  aboGpv: string;
  personalBV: string;
  bonusPercent: string;
  personalBonusAmount: string;
  nonBonusable: string;
  total: string;
};

export type PerformanceTotal = {
  title: string;
  subTitle: string;
  zeroAmountText: string;
  table: Partial<Table>;
  exportTitle: string;
};

export type ABOContent = {
  ConfidentialUser: string;
  notAvailable: string;
};

export type CommonContent = {
  bonusesDisclaimer: string;
  totalsDisclaimer: string;
  footer: string;
};

export type BonusData = {
  bonusPercent: BonusPercent;
  differentialBonus: DifferentialBonusEntry[];
};

export const findNonBonusableAmount = (isoCurrencyCode: string, groupNonBonusable: GroupNonBonusableEntry[]) => {
  const nonBonusable = groupNonBonusable?.find(
    (item: { isoCurrencyCode: string }) => isoCurrencyCode === item.isoCurrencyCode,
  );
  return nonBonusable?.groupBV?.formatted || "0.00";
};

const isZeroAmount = (formattedAmount?: string): boolean =>
  !!formattedAmount && /^[^0-9]*0+\.?0*[^0-9]*$/.test(formattedAmount);

const DifferentialBonusModal = ({
  openModal,
  onCloseModal,
  performanceData,
  performanceTotal,
  isFetching,
  isError,
  onRetry,
}: {
  openModal: boolean;
  onCloseModal: () => void;
  performanceData?: Partial<MonthlyPerformanceResponse>;
  performanceTotal?: Partial<BonusBreakdownLineData>;
  isFetching: boolean;
  isError: boolean;
  onRetry?: () => void;
}) => {
  const { t } = useTranslation(["monthlyPerformanceTotalModals", "leafAboName", "common"]);
  const user = useAppSelector((state) => state.boot?.user);
  const configuration = useAppSelector((state) => state.boot?.configuration);
  const anaMarket = ANA_MARKET_CODES.includes(user.aff);
  const { selectedPeriod } = useAppSelector((state) => state.period);

  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isPdfError, setIsPdfError] = useState(false);

  const locale = storedLocale();

  const contents = t("differential", {
    ns: "monthlyPerformanceTotalModals",
    returnObjects: true,
    defaultValue: defaultContent.differential,
  }) as PerformanceTotal;

  /**
   * Generate PDF using server-side API endpoint
   * Replaces the previous Web Worker-based client-side generation
   */
  const generatePDF = async (): Promise<Blob> => {
    // Get API URLs from configuration
    const baseUrl = configuration.apiBaseUrl + (configuration.apiBasePaths?.monthlyDash ?? "");
    const masheryDomain = configuration.requestUrlBase;
    const accessToken = user.access_token;

    // Prepare content payload with all translated labels
    const contentPayload = prepareDifferentialBonusContent(t);

    // Format affAbo parameter
    const affAbo = `${user.aff}-${user.abo}`;

    // Business context headers (required for API access)
    const selectedBusiness = affAbo;

    // Call API to generate PDF
    const blob = await exportDifferentialBonusPDF(
      affAbo,
      selectedPeriod,
      locale,
      user.isoCountryCode, // countryCode
      contentPayload,
      baseUrl,
      accessToken,
      selectedBusiness,
      selectedPeriod,
      masheryDomain,
    );

    return blob;
  };

  const renderLoader = (children: React.ReactNode) => (
    <LeafRectangleLoader isLoading={isFetching} height="2rem" width="auto">
      {children}
    </LeafRectangleLoader>
  );

  if (isZeroAmount(performanceTotal?.amount)) {
    return (
      <div className="monthly-bonus-modal">
        <LeafModal
          title={contents?.title}
          open={openModal}
          onCloseCallbackFn={onCloseModal}
          id={"monthly-bonus-modal differential-bonus-modal--zero-amount"}
        >
          <p className="differential-bonus-modal__zero-amount-text">
            <Trans
              defaults={contents?.zeroAmountText}
              components={{
                br: <br />,
              }}
            />
          </p>
        </LeafModal>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="monthly-bonus-modal">
        <LeafModal title={contents?.title} open={openModal} onCloseCallbackFn={onCloseModal} id={"monthly-bonus-modal"}>
          <ErrorTile
            clickFunction={() => onRetry?.()}
            errorStatus={isError}
            isLoading={isFetching}
            tealiumTrackId="Differential_Bonus_Modal"
          />
        </LeafModal>
      </div>
    );
  }

  const downloadOnClick = async () => {
    setIsGeneratingPDF(true);
    try {
      const blob = await generatePDF();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Differential_Bonus_${selectedPeriod}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch {
      setIsPdfError(true);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const RenderDownloadButton = () => {
    if (isGeneratingPDF === true) return <LeafRectangleLoader isLoading={true} width={"8rem"} height={"2rem"} />;

    if (isPdfError === true) return <IconAlert />;

    return (
      <LeafLinkButton
        as="button"
        onClick={downloadOnClick}
        disabled={false}
        label={contents?.exportTitle}
        fontSize="medium"
        fontWeight="bold"
        theme="white"
      >
        <div className="differential-bonus-modal__export-title-section">
          <span className="differential-bonus-modal__export-title">{contents?.exportTitle}</span>
          <span>
            <IconExportPdf className="differential-bonus-modal__export-icon" outline={true} />
          </span>
        </div>
      </LeafLinkButton>
    );
  };

  return (
    <div className="monthly-bonus-modal">
      <LeafModal title={contents?.title} open={openModal} onCloseCallbackFn={onCloseModal} id={"monthly-bonus-modal"}>
        <p className="monthly-bonus-modal__subtitle">
          <Trans defaults={contents?.subTitle} components={{ 1: <strong /> }} />
        </p>
        <div className="monthly-bonus-modal_content">
          <div className="monthly-bonus-modal_bonus-details">
            {renderLoader(
              performanceData?.differentialBonus?.map((item: DifferentialBonusEntry) => (
                <div className="wrapper" key={item.aboName}>
                  <div className="abo">
                    <span>
                      {item.displayName ? (
                        getDisplayName(item.displayName, t)
                      ) : (
                        <ABOName
                          name={item.aboName}
                          localName={item.localizedName}
                          privacyFlag={item.privacyFlag}
                          languageCode={item.isoLanguageCode}
                          locale={locale}
                        />
                      )}
                    </span>
                    <div className="abo-details">
                      <span>
                        <Trans
                          defaults={contents?.table?.aboBonusPercent}
                          values={{ aboBonusPercent: item.bonusPercent.formatted }}
                        />
                      </span>
                      <span> | </span>
                      <span>
                        <Trans defaults={contents?.table?.aboGpv} values={{ aboGpv: item.totalGPV.formatted }} />
                      </span>
                    </div>
                  </div>
                  {item.groupBonus?.map((groupItem: GroupEntry, index: number) => (
                    <div key={`${item.aboName}-${groupItem.isoCurrencyCode}-${index}`}>
                      <div className="personal-bv">
                        <span>
                          <Trans
                            defaults={contents?.table?.personalBV}
                            values={{ countryCode: groupItem.isoCountryCode }}
                          />
                        </span>
                        <span>{groupItem.groupBV.formatted}</span>
                      </div>
                      <div className="bonus-percent">
                        <span>
                          <Trans
                            defaults={contents?.table?.bonusPercent}
                            values={{
                              bonusPercent: `${performanceData?.bonusPercent?.formatted} - ${item.bonusPercent?.formatted}`,
                            }}
                          />
                        </span>
                        <span>x {item.differentialPercent?.formatted}</span>
                      </div>
                      <div className="current-total">
                        <span>
                          <strong>{contents?.table?.personalBonusAmount}</strong>
                        </span>
                        <span>{groupItem.differentialAmount.formatted}</span>
                      </div>
                      {anaMarket && (
                        <div className="non-bonusable-leg">
                          <span>{contents?.table?.nonBonusable}</span>
                          <span className="non-bonusable-leg_content">
                            <span>{findNonBonusableAmount(groupItem.isoCurrencyCode, item.groupNonBonusable)}</span>
                            <IconAlert />
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )),
            )}
          </div>
        </div>
        <div className="differential-bonus-modal__sticky-footer">
          {renderLoader(
            <div className="differential-bonus-modal__footer-content">
              <div className="differential-bonus-modal__total">
                <span className="differential-bonus-modal__total-text">{contents?.table?.total}</span>
                <span className="differential-bonus-modal__total-amount">{performanceTotal?.amount}</span>
              </div>
              {isFetching === false && performanceData?.differentialBonus && (
                <div className="differential-bonus-modal__footer-export">
                  <RenderDownloadButton />
                </div>
              )}
            </div>,
          )}
        </div>
        <BonusInfoFooter hideDate={true} />
      </LeafModal>
    </div>
  );
};

export default DifferentialBonusModal;
